import {
  buildPath,
  buildTemplatePath,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  pn,
  ScreenType,
  Stack,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  Permission,
  StudentServiceType,
  StudentServiceTypeEditResource,
  StudentServiceTypeViewResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { BasicInfo } from "./BasicInfo";
import { ServiceSubTypes } from "./ServiceSubTypes/ServiceSubTypes";
import { StudentServiceTypePageHeader } from "./StudentServiceTypePageHeader";

const routeName = "studentServiceType";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

export const studentServiceTypePageRoute: PageRoute<Params> = {
  handle: "Service Type",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const StudentServiceTypePage = () => {
  const ROOT_PERMISSION: Permission = Permission.SystemUtility;
  const navigate = useNavigate();
  const { params } = useParams(routeName, Params);
  const [serviceType, setServiceType] = useState<StudentServiceType>({} as StudentServiceType);
  const [initServiceType, setInitServiceType] = useState<StudentServiceType>(serviceType);
  const [viewResource, setViewResource] = useState<StudentServiceTypeViewResource>({});
  const [editResource, setEditResource] = useState<StudentServiceTypeEditResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [serviceType] });
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);

    ApiService.studentServiceTypes.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") {
      ApiService.studentServiceTypes.getEditResource().then((response) => {
        setEditResource(response);
      });
    }
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${studentServiceTypePageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

  const fetch = (id: number) => {
    ApiService.studentServiceTypes.get(id).then((response) => {
      setServiceType(response);
      setInitServiceType(response);
      resetUnsavedChanges([response]);
    });
    };

    const pageHeader = (
        <StudentServiceTypePageHeader
            rootPermission={ROOT_PERMISSION}
            handle={studentServiceTypePageRoute.handle}
            crudMode={params.crudMode}
            reroute={reroute}
            fetch={fetch}
            serviceType={serviceType}
            setServiceType={setServiceType}
            initServiceType={initServiceType}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            resetUnsavedChanges={resetUnsavedChanges}
        />
    );

    const pageBody = (
        <Stack spacing="lg">
            <BasicInfo
                crudMode={params.crudMode}
                serviceType={serviceType}
                setServiceType={setServiceType}
                viewResource={viewResource}
                editResource={editResource}
                validationErrors={validationErrors}
            />

            <ServiceSubTypes
                rootPermission={ROOT_PERMISSION}
                crudMode={params.crudMode}
                serviceSubTypes={serviceType.manualServiceSubTypes || []}
                setServiceSubTypes={(value) =>
                    setServiceType(
                        produce(serviceType, (draft) => {
                            draft.manualServiceSubTypes = value;
                        })
                    )
                }
                validationErrors={validationErrors[pn<StudentServiceType>("manualServiceSubTypes")] || []}
                generalValidationErrors={
                    validationErrors[pn<StudentServiceType>("manualServiceSubTypesGeneralValidationErrors")] || []
                }
                onEdit={() => reroute(serviceType.id, "edit")}
            />
        </Stack>
    );

  return (
      <Dialog
          isForm
          open={true}
          onClose={() => handleClose(navigate, unsavedChanges)}
          maxWidth="md"
          header={pageHeader}
          content={pageBody}
      />
  );
};
